import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { Footer } from '@/components/layout/Footer';
import { Navbar } from '@/components/layout/Navbar';
import { ErrorState, PageLoader } from '@/components/ui/Primitives';
import { usePageView } from '@/hooks/usePageView';
import { usePortfolio } from '@/hooks/usePortfolio';
import { HomePage } from '@/pages/HomePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import type { Bootstrap } from '@/lib/types';
import { useI18n } from '@/store/preferences';

// The admin bundle is fetched only when someone actually opens /admin.
const AdminApp = lazy(() =>
  import('@/pages/admin/AdminApp').then((module) => ({ default: module.AdminApp }))
);

// These routes pull in the Markdown renderer, which is heavier than the rest of
// the site combined. Splitting them keeps the landing page payload small.
const AboutPage = lazy(() =>
  import('@/pages/AboutPage').then((module) => ({ default: module.AboutPage }))
);
const ProjectDetailPage = lazy(() =>
  import('@/pages/ProjectDetailPage').then((module) => ({ default: module.ProjectDetailPage }))
);
const CertificatesPage = lazy(() =>
  import('@/pages/CertificatesPage').then((module) => ({ default: module.CertificatesPage }))
);
const ContactPage = lazy(() =>
  import('@/pages/ContactPage').then((module) => ({ default: module.ContactPage }))
);

/** Reads a public site setting out of the bootstrap payload. */
function settingOf(data: Bootstrap, key: string): string | null {
  return data.settings.find((setting) => setting.key === key)?.value?.trim() || null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Block body on purpose: a concise arrow would return whatever scrollTo
    // returns, and React treats an effect's return value as its cleanup
    // function — then calls it on the next route change.
    // The two-argument form also avoids the `behavior: 'instant'` enum, which
    // older engines reject with a TypeError.
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

export function App() {
  const { pathname } = useLocation();
  usePageView();

  if (pathname.startsWith('/admin')) {
    return (
      <Suspense fallback={<PageLoader label="admin" />}>
        <AdminApp />
      </Suspense>
    );
  }

  return <PublicSite />;
}

function PublicSite() {
  const { t } = useI18n();
  const { data, loading, error, reload } = usePortfolio();

  if (loading) return <PageLoader label={t.common.loading} />;

  if (error || !data) {
    return <ErrorState message={error ?? t.common.error} action={reload} actionLabel={t.common.retry} />;
  }

  return (
    <>
      <ScrollToTop />
      <Navbar name={data.profile.fullName} logoUrl={settingOf(data, 'site.logo_image')} />

      <main id="main">
        <Suspense fallback={<PageLoader label={t.common.loading} />}>
          <Routes>
            <Route path="/" element={<HomePage data={data} />} />
            <Route path="/projects" element={<ProjectsPage data={data} />} />
            <Route path="/projects/:slug" element={<ProjectDetailPage data={data} />} />
            <Route path="/about" element={<AboutPage data={data} />} />
            <Route path="/certificates" element={<CertificatesPage data={data} />} />
            <Route path="/contact" element={<ContactPage data={data} />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>

      <Footer
        name={data.profile.fullName}
        email={data.profile.email}
        socialLinks={data.socialLinks}
      />
    </>
  );
}
